import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BotIcon } from '@/components/ui/bot-icon';
import { DemoPill, StateMessage } from '@/components/ui/controls';
import { Card, Field, PageHeader } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import { formatDate } from '@/utils/format';
import http from '@/utils/http';
import { useApi, useDebounced } from '@/utils/use-api';

import { CATEGORY_LABELS, SHORT_ANSWER_MAX, type ArticleCategory, type KBArticle, type KBMatch } from './api-types';

const back = { to: '/knowledge', label: 'Knowledge base' };
const CATEGORIES = Object.keys(CATEGORY_LABELS) as ArticleCategory[];

/** The bot's reply as the client gets it, once a person confirms the draft: just the short answer (REM-2). */
const BotReply = ({ text }: { text: string }) => (
  <div className="text-sm">
    <p className="inline-flex items-center gap-1 text-xs text-fg-muted">
      <BotIcon />
      Prisma bot
    </p>
    <p className="mt-0.5 rounded-lg bg-info/15 px-2.5 py-1.5 text-fg">{text.trim() || '…'}</p>
  </div>
);

/** Which saved article a client's question would match (POST /knowledge/match), once typing pauses. */
const useMatches = (question: string) => {
  const asked = useDebounced(question.trim(), 300);
  const [matches, setMatches] = useState<KBMatch[] | null>(null);
  useEffect(() => {
    if (!asked) {
      setMatches(null);
      return;
    }
    let cancelled = false;
    http
      .post<KBMatch[]>('/knowledge/match', { question: asked })
      .then((r) => !cancelled && setMatches(r.data))
      .catch(() => !cancelled && setMatches([]));
    return () => {
      cancelled = true;
    };
  }, [asked]);
  return asked ? matches : null;
};

/**
 * Article editor (REM-2, REM-3): the short answer the bot gives for a matching question,
 * at once on Telegram and as a draft by email (Q19), the full text,
 * the keywords a question may use, and a box to try which article a question matches.
 * On a phone, Save and Delete sit in a bar above the bottom tabs (UI-1).
 */
const Editor = ({ article }: { article?: KBArticle }) => {
  const navigate = useNavigate();
  // A new article can start from a question the bot answered badly (?title=, from Bot answers).
  const [params] = useSearchParams();
  const [title, setTitle] = useState(article?.title ?? params.get('title') ?? '');
  const [category, setCategory] = useState<ArticleCategory>(article?.category ?? 'onboarding');
  const [shortAnswer, setShortAnswer] = useState(article?.short_answer ?? '');
  const [body, setBody] = useState(article?.body ?? '');
  const [keywords, setKeywords] = useState((article?.keywords ?? []).join(', '));
  const [state, setState] = useState<'idle' | 'busy' | 'saved' | 'invalid' | 'failed'>('idle');
  const [question, setQuestion] = useState('');
  const matches = useMatches(question);
  const best = matches?.[0];

  const edit =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      if (state !== 'busy') setState('idle');
    };

  const save = async () => {
    setState('busy');
    const payload = { title, category, short_answer: shortAnswer, body, keywords: keywords.split(',') };
    try {
      if (article) {
        await http.put(`/knowledge/articles/${encodeURIComponent(article.id)}`, payload);
        setState('saved');
      } else {
        const r = await http.post<KBArticle>('/knowledge/articles', payload);
        navigate(`/knowledge/${encodeURIComponent(r.data.id)}`, { replace: true });
      }
    } catch (err: unknown) {
      setState((err as { response?: { status?: number } }).response?.status === 422 ? 'invalid' : 'failed');
    }
  };

  const remove = async () => {
    if (!article || !window.confirm('Delete this article? Answers that used it keep their text.')) return;
    setState('busy');
    try {
      await http.delete(`/knowledge/articles/${encodeURIComponent(article.id)}`);
      navigate('/knowledge');
    } catch {
      setState('failed');
    }
  };

  const NOTE = { saved: 'Saved.', invalid: 'Check the title and the short answer.', failed: 'That did not work. Try again.' };
  const note = (state === 'saved' || state === 'invalid' || state === 'failed') && (
    <span className={state === 'saved' ? 'text-sm text-fg-muted' : 'text-sm text-danger'}>{NOTE[state]}</span>
  );
  const buttons = (
    <>
      {article && (
        <button type="button" className={btn.danger} disabled={state === 'busy'} onClick={remove}>
          Delete
        </button>
      )}
      <button type="button" className={btn.primary} disabled={state === 'busy' || !title.trim() || !shortAnswer.trim()} onClick={save}>
        Save
      </button>
    </>
  );

  return (
    <div className="pb-20 md:pb-0">
      <PageHeader
        back={back}
        title={article ? article.title : 'New article'}
        actions={
          <>
            {article?.demo && <DemoPill title="Made up by the local demo seed">Demo</DemoPill>}
            <span className="hidden md:contents">
              {note}
              {buttons}
            </span>
          </>
        }
      />
      <div className="fixed inset-x-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-20 flex items-center justify-end gap-2 bg-surface px-4 py-2 shadow-up md:hidden">
        <span className="mr-auto min-w-0">{note}</span>
        {buttons}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card title="Article">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_200px]">
              <label className="block">
                <span className={field.label}>Title</span>
                <input className={field.input} value={title} onChange={(e) => edit(setTitle)(e.target.value)} placeholder="The question clients ask" />
              </label>
              <label className="block">
                <span className={field.label}>Category</span>
                <select className={`${field.select} w-full`} value={category} onChange={(e) => edit(setCategory)(e.target.value as ArticleCategory)}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="mb-1 flex items-baseline justify-between text-xs text-fg-muted">
                Short answer the bot gives
                <span className="tabular-nums text-fg-faint">
                  {shortAnswer.length} / {SHORT_ANSWER_MAX}
                </span>
              </span>
              <textarea
                className={field.textarea}
                rows={3}
                maxLength={SHORT_ANSWER_MAX}
                value={shortAnswer}
                onChange={(e) => edit(setShortAnswer)(e.target.value)}
                placeholder="One or two sentences."
              />
            </label>
            <div>
              <p className={field.label}>How the client gets it, once a person confirms</p>
              <BotReply text={shortAnswer} />
            </div>

            <label className="block">
              <span className={field.label}>Keywords, separated by commas: other words a question may use for this article</span>
              <input className={field.input} value={keywords} onChange={(e) => edit(setKeywords)(e.target.value)} placeholder="license, curacao, weeks" />
            </label>

            <label className="block">
              <span className={field.label}>Full text, for the team and for longer answers</span>
              <textarea className={field.textarea} rows={8} value={body} onChange={(e) => edit(setBody)(e.target.value)} />
            </label>
          </div>
        </Card>

        <div className="space-y-6">
          <Card title="Try a question">
            <input
              className={field.input}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="How long does the license take?"
              aria-label="A question a client might ask"
            />
            <p className="mt-1 text-xs text-fg-faint">It matches the saved articles: save first to try your changes.</p>
            <div className="mt-3">
              {!question.trim() ? (
                <p className="text-sm text-fg-faint">Type a question a client might ask, to see which article the bot would use.</p>
              ) : matches === null ? (
                <p className="text-sm text-fg-faint">Looking…</p>
              ) : best?.answers ? (
                <>
                  <p className="mb-2 text-sm text-fg-muted">
                    The bot would use{' '}
                    <Link to={`/knowledge/${encodeURIComponent(best.article.id)}`} className="text-fg-strong hover:text-brand">
                      {best.article.title}
                    </Link>
                    {best.article.id === article?.id && ' (this one)'}.
                  </p>
                  <BotReply text={best.article.short_answer} />
                </>
              ) : (
                <p className="text-sm text-fg-muted">
                  {best ? `“${best.article.title}” shares only one word with it, too little: the bot` : 'No article matches well enough, so the bot'} drafts nothing and the
                  team answers as usual.
                </p>
              )}
            </div>
          </Card>

          {article && (
            <Card title="Usage">
              <dl>
                <Field label="Answers using it">{article.times_used}</Field>
                <Field label="Updated">{formatDate(article.updated_at)}</Field>
                {article.created_by && <Field label="Written by">{article.created_by}</Field>}
              </dl>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

/** One article (GET /knowledge/articles/{id}), or a new one; a fresh editor per article. */
const ArticleEditorPage = () => {
  const { id } = useParams();
  const { data, error } = useApi<KBArticle>(id ? `/knowledge/articles/${encodeURIComponent(id)}` : null);

  if (id && error && !data) {
    return (
      <div>
        <PageHeader title="Article" back={back} />
        {error === 'Not found.' ? (
          <StateMessage title="Article not found">
            There is no article with this id.{' '}
            <Link to="/knowledge" className={btn.link}>
              Back to the knowledge base
            </Link>
          </StateMessage>
        ) : (
          <StateMessage title="The article could not be loaded" tone="error">
            {error}
          </StateMessage>
        )}
      </div>
    );
  }
  if (id && !data) {
    return (
      <div>
        <PageHeader title="Loading…" back={back} />
        <div className="h-64 animate-pulse rounded-card bg-surface shadow-e1" />
      </div>
    );
  }
  return <Editor key={data?.id ?? 'new'} article={data ?? undefined} />;
};

export default ArticleEditorPage;
